import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardNavbar } from "@/components/DashboardNavbar";
import { StatusBadge, TypeBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
   Building2, Briefcase, FolderOpen, Layers, Mail, Megaphone, Trash2, Plus, X,
  ShieldAlert, ShieldX, TrendingUp, FileText, FileSpreadsheet, BellRing, Loader2, AlertTriangle, Globe,
} from "lucide-react";
import AdminSmmeDatabase from "@/components/admin/AdminSmmeDatabase";
import AdminReports from "@/components/admin/AdminReports";
import AdminCompliance from "@/components/admin/AdminCompliance";
import AdminNotifications from "@/components/admin/AdminNotifications";
import AdminTraffic from "@/components/admin/AdminTraffic";
import AdminVideoUploader from "@/components/admin/AdminVideoUploader";

const StatCard = ({ icon: Icon, label, value, sub, tone = "slate", onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md" : "cursor-default"}`}
    data-testid={`stat-${label}`}
  >
    <div className="flex items-start justify-between">
      <span className="text-xs font-bold uppercase leading-tight tracking-wide text-slate-500">{label}</span>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-${tone}-100`}><Icon className={`h-4 w-4 text-${tone}-600`} /></span>
    </div>
    <div className="mt-3 font-display text-4xl font-extrabold text-slate-900">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    {onClick && <span className="mt-3 block text-xs font-semibold text-blue-600" data-testid={`stat-link-${label}`}>VIEW LIST →</span>}
  </button>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [visits, setVisits] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [opps, setOpps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [notices, setNotices] = useState([]);
  const [notice, setNotice] = useState({ title: "", body: "", category: "Announcements" });
  const [newCat, setNewCat] = useState("");
  const [runningExpiry, setRunningExpiry] = useState(false);
  const [purgingBots, setPurgingBots] = useState(false);
  const [tab, setTab] = useState("database");
  const [delBuyer, setDelBuyer] = useState(null);
  const [deletingBuyer, setDeletingBuyer] = useState(false);
  const [complianceFilter, setComplianceFilter] = useState("expiring");
  const openCompliance = (f) => { setComplianceFilter(f); setTab("compliance"); };

  const load = useCallback(async () => {
    const [st, sup, buy, op, cat, nt, vis] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/suppliers"), api.get("/admin/buyers"),
      api.get("/opportunities"), api.get("/categories"), api.get("/notices"), api.get("/visits"),
    ]);
    setStats(st.data); setSuppliers(sup.data); setBuyers(buy.data); setOpps(op.data); setCategories(cat.data); setNotices(nt.data); setVisits(vis.data?.count ?? 0);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const exportSmme = async (fmt) => {
    try {
      const res = await api.get(`/export/suppliers?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `smme-database.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const runExpiry = async () => {
    setRunningExpiry(true);
    try { await api.post("/admin/run-expiry-check"); toast.success("Expiry check triggered — reminders are being sent"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunningExpiry(false); }
  };
  const purgeBots = async () => {
    setPurgingBots(true);
    try {
      const { data } = await api.post("/admin/purge-bots");
      toast.success(`Purged ${data.accounts} bot account${data.accounts === 1 ? "" : "s"}, ${data.visit_logs} bot visit${data.visit_logs === 1 ? "" : "s"} and ${data.login_attempts} login attempt${data.login_attempts === 1 ? "" : "s"}.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPurgingBots(false); }
  };
  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/categories", { name: newCat.trim() }); setNewCat(""); toast.success("Category added"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteCategory = async (name) => {
    try { await api.delete(`/categories/${encodeURIComponent(name)}`); toast.success("Category removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteOpp = async (id) => {
    try { await api.delete(`/opportunities/${id}`); toast.success("Opportunity deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const postNotice = async () => {
    if (!notice.title.trim() || !notice.body.trim()) { toast.error("Title and message are required"); return; }
    try { await api.post("/notices", notice); setNotice({ title: "", body: "", category: "Announcements" }); toast.success("Notice posted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteNotice = async (id) => {
    try { await api.delete(`/notices/${id}`); toast.success("Notice removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const confirmDeleteBuyer = async () => {
    setDeletingBuyer(true);
    try { await api.delete(`/admin/buyers/${delBuyer.id}`); toast.success(`${delBuyer.company_name} removed`); setDelBuyer(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDeletingBuyer(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <DashboardNavbar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Administrator</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Operations Console</h1>
          <p className="mt-1 text-slate-500">Network activity, compliance posture and notification trail.</p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700" data-testid="admin-identity">Admin · {user?.email}</span>
            <Button variant="outline" size="sm" onClick={() => exportSmme("pdf")} className="gap-1.5" data-testid="console-export-pdf"><FileText className="h-4 w-4 text-red-600" /> Export to PDF</Button>
            <Button variant="outline" size="sm" onClick={() => exportSmme("xlsx")} className="gap-1.5" data-testid="console-export-excel"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Export to Excel</Button>
            <Button variant="outline" size="sm" onClick={runExpiry} disabled={runningExpiry} className="gap-1.5" data-testid="console-run-expiry">{runningExpiry ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Run expiry check</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={purgingBots} className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="console-purge-bots">{purgingBots ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Purge bot accounts</Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="purge-bots-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Purge bot &amp; test accounts?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes all automated test accounts (emails ending in <strong>@test.com</strong> or <strong>@wonder.com</strong>) along with any opportunities or quotes they created. It also clears bot visit records (headless browsers) and resets failed-login history. Your real accounts are not affected. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="purge-bots-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={purgeBots} className="bg-red-600 hover:bg-red-700" data-testid="purge-bots-confirm">Yes, purge them</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard icon={Building2} label="Total SMMEs" value={stats.total_suppliers ?? 0} sub={`${stats.smmes_month ?? 0} this month`} tone="indigo" onClick={() => setTab("database")} />
            <StatCard icon={Briefcase} label="Total Buyers" value={stats.total_buyers ?? 0} sub={`${stats.buyers_month ?? 0} this month`} tone="blue" onClick={() => setTab("buyers")} />
            <StatCard icon={FolderOpen} label="Opportunities Posted" value={stats.total_opportunities ?? 0} sub={`${stats.opportunities_month ?? 0} this month`} tone="emerald" onClick={() => setTab("opportunities")} />
            <StatCard icon={Mail} label="Email Notifications" value={stats.notifications_total ?? 0} sub={`${stats.notifications_month ?? 0} this month`} tone="amber" onClick={() => setTab("notifications")} />
            <StatCard icon={Globe} label="Site Visits" value={visits} sub="See locations & devices" tone="violet" onClick={() => setTab("traffic")} />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <button type="button" onClick={() => openCompliance("expiring")} className="rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md" data-testid="console-compliance">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-amber-600"><ShieldAlert className="h-4 w-4" /> Compliance</div>
              <div className="mt-2 font-display text-3xl font-extrabold text-amber-600">{stats.docs_expiring ?? 0}</div>
              <div className="text-sm text-slate-500">documents expiring within 30 days</div>
              <span className="mt-3 block text-xs font-semibold text-blue-600">VIEW LIST →</span>
            </button>
            <button type="button" onClick={() => openCompliance("expired")} className="rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-red-300 hover:shadow-md" data-testid="console-expired">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-red-600"><ShieldX className="h-4 w-4" /> Expired</div>
              <div className="mt-2 font-display text-3xl font-extrabold text-red-600">{stats.docs_expired ?? 0}</div>
              <div className="text-sm text-slate-500">documents currently expired</div>
              <span className="mt-3 block text-xs font-semibold text-blue-600">VIEW LIST →</span>
            </button>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="console-trend">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-600"><TrendingUp className="h-4 w-4" /> Trend</div>
              <div className="mt-1 text-xs text-slate-500">SMME registrations · last months</div>
              <TrendSpark data={stats.registration_trend || []} />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AdminVideoUploader title="Welcome-page SMME video" infoUrl="/smme-video" uploadUrl="/admin/smme-video" deleteUrl="/admin/smme-video" testid="admin-smme-video" />
            <AdminVideoUploader title="Welcome-page Buyer video" infoUrl="/buyer-video" uploadUrl="/admin/buyer-video" deleteUrl="/admin/buyer-video" testid="admin-buyer-video" />
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mt-8">
            <TabsList data-testid="admin-tabs">
              <TabsTrigger value="database" data-testid="tab-database">SMME Database</TabsTrigger>
              <TabsTrigger value="buyers" data-testid="tab-buyers">Buyers</TabsTrigger>
              <TabsTrigger value="opportunities" data-testid="tab-admin-opps">Opportunities</TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
              <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
              <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
              <TabsTrigger value="traffic" data-testid="tab-traffic">Traffic</TabsTrigger>
              <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
              <TabsTrigger value="notices" data-testid="tab-admin-notices">Notice Board</TabsTrigger>
            </TabsList>

            <TabsContent value="database" className="mt-6">
              <AdminSmmeDatabase suppliers={suppliers} sectors={categories} reload={load} />
            </TabsContent>

            <TabsContent value="buyers" className="mt-6">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm" data-testid="buyers-table">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr><th className="px-5 py-3">Company</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Phone</th><th className="px-5 py-3 text-right"></th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {buyers.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No buyers registered yet.</td></tr>}
                    {buyers.map((b) => (
                      <tr key={b.id} className="hover:bg-slate-50" data-testid={`buyer-row-${b.id}`}>
                        <td className="px-5 py-3 font-medium text-slate-800">{b.company_name}</td>
                        <td className="px-5 py-3 text-slate-600">{b.contact_person || b.name}</td>
                        <td className="px-5 py-3 text-slate-600">{b.email}</td>
                        <td className="px-5 py-3 text-slate-600">{b.phone || "—"}</td>
                        <td className="px-5 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => setDelBuyer(b)} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-buyer-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="opportunities" className="mt-6">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm" data-testid="admin-opps-table">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr><th className="px-5 py-3">Reference</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Title</th><th className="px-5 py-3">Buyer</th><th className="px-5 py-3">Responses</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {opps.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No opportunities posted yet.</td></tr>}
                    {opps.map((o) => (
                      <tr key={o.id} className="hover:bg-slate-50" data-testid={`admin-opp-row-${o.id}`}>
                        <td className="px-5 py-3 font-mono text-xs text-slate-500">{o.reference}</td>
                        <td className="px-5 py-3"><TypeBadge type={o.type} /></td>
                        <td className="px-5 py-3 font-medium text-slate-800">{o.title}</td>
                        <td className="px-5 py-3 text-slate-600">{o.buyer_company}</td>
                        <td className="px-5 py-3 font-mono">{o.response_count}</td>
                        <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                        <td className="px-5 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => deleteOpp(o.id)} className="text-red-600" data-testid={`admin-delete-opp-${o.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="reports" className="mt-6">
              <AdminReports opps={opps} notices={notices} />
            </TabsContent>

            <TabsContent value="compliance" className="mt-6">
              <AdminCompliance suppliers={suppliers} filter={complianceFilter} onFilterChange={setComplianceFilter} />
            </TabsContent>

            <TabsContent value="notifications" className="mt-6">
              <AdminNotifications />
            </TabsContent>

            <TabsContent value="traffic" className="mt-6">
              <AdminTraffic />
            </TabsContent>

            <TabsContent value="categories" className="mt-6">
              <div className="max-w-2xl rounded-xl border border-slate-200 bg-white p-6">
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Layers className="h-5 w-5 text-amber-600" /> Supply categories</h3>
                <p className="text-sm text-slate-500">These appear in supplier registration and opportunity posting.</p>
                <div className="mt-4 flex gap-2">
                  <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Add a new category" data-testid="new-category-input" onKeyDown={(e) => e.key === "Enter" && addCategory()} />
                  <Button onClick={addCategory} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="add-category-button"><Plus className="h-4 w-4" /> Add</Button>
                </div>
                <div className="mt-5 flex flex-wrap gap-2" data-testid="categories-list">
                  {categories.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-3 pr-1.5 text-sm text-slate-700" data-testid={`category-chip-${c}`}>
                      {c}
                      <button onClick={() => deleteCategory(c)} className="rounded-full p-0.5 text-slate-400 hover:bg-red-100 hover:text-red-600" data-testid={`delete-category-${c}`}><X className="h-3.5 w-3.5" /></button>
                    </span>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notices" className="mt-6">
              <div className="max-w-2xl rounded-xl border border-slate-200 bg-white p-6">
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Megaphone className="h-5 w-5 text-amber-600" /> Post a notice</h3>
                <p className="text-sm text-slate-500">Notices appear on every supplier's Notice Board.</p>
                <div className="mt-4 space-y-3">
                  <div>
                    <Label>Notice type</Label>
                    <Select value={notice.category} onValueChange={(v) => setNotice((n) => ({ ...n, category: v }))}>
                      <SelectTrigger className="mt-1.5" data-testid="notice-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Meetings">Meetings</SelectItem>
                        <SelectItem value="Announcements">Announcements</SelectItem>
                        <SelectItem value="Proposals">Proposals</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input value={notice.title} onChange={(e) => setNotice((n) => ({ ...n, title: e.target.value }))} placeholder="Notice title" data-testid="notice-title-input" />
                  <Textarea value={notice.body} onChange={(e) => setNotice((n) => ({ ...n, body: e.target.value }))} rows={4} placeholder="Write your announcement…" data-testid="notice-body-input" />
                  <Button onClick={postNotice} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="post-notice-button"><Plus className="h-4 w-4" /> Post notice</Button>
                </div>
              </div>
              <div className="mt-6 space-y-3" data-testid="admin-notices-list">
                {notices.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-500">No notices posted yet.</p>}
                {notices.map((n) => (
                  <div key={n.id} className="flex items-start justify-between rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`admin-notice-${n.id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-display font-bold text-slate-900">{n.title}</h4>
                        <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{n.category || "Announcements"}</span>
                      </div>
                      <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{n.body}</p>
                      <p className="mt-2 text-xs font-medium text-slate-400">{n.author} · {new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deleteNotice(n.id)} className="text-red-600" data-testid={`delete-notice-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>

        {delBuyer && (
          <Dialog open onOpenChange={() => !deletingBuyer && setDelBuyer(null)}>
            <DialogContent data-testid="admin-buyer-delete-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display"><AlertTriangle className="h-5 w-5 text-red-600" /> Delete buyer</DialogTitle>
                <DialogDescription>
                  This permanently removes <span className="font-semibold text-slate-800">{delBuyer.company_name}</span> and all opportunities they posted. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDelBuyer(null)} disabled={deletingBuyer} data-testid="admin-buyer-delete-cancel">Cancel</Button>
                <Button onClick={confirmDeleteBuyer} disabled={deletingBuyer} className="gap-1 bg-red-600 hover:bg-red-700" data-testid="admin-buyer-delete-confirm">
                  {deletingBuyer ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete buyer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
}

function TrendSpark({ data }) {
  if (!data.length) return <div className="mt-3 text-sm text-slate-400">No data yet</div>;
  const max = Math.max(1, ...data.map((d) => d.count));
  const w = 240, h = 56, step = data.length > 1 ? w / (data.length - 1) : w;
  const pts = data.map((d, i) => `${i * step},${h - (d.count / max) * (h - 6) - 3}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="mt-3 w-full" preserveAspectRatio="none" data-testid="trend-spark">
      <polyline fill="none" stroke="#2563eb" strokeWidth="2.5" points={pts} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
